import type { BoxProps } from '@mui/material/Box';
import { varFade, MotionViewport } from '@/components/animate';
import { Carousel, useCarousel, CarouselArrowFloatButtons } from '@/components/carousel';
import { Image } from '@/components/image';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { m } from 'framer-motion';
import { CarouselSmallSection } from '@/types/sections/CarouselSmallSection';
import { CardItem } from '@/types/CardItem';
import { useRef, useEffect } from 'react';
import { alpha, Button, useTheme } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getCtaUrl } from '@/types/Cta';
import Icon, { getIconName } from '@/components/Icon/Icon';

// ----------------------------------------------------------------------

type CarouselSmallProps = BoxProps & {
  section: CarouselSmallSection;
};

export default function CarouselSmall({ sx, section, ...other }: CarouselSmallProps) {
  const carousel = useCarousel({
    align: 'start',
    slideSpacing: '24px',
    slidesToShow: {
      xs: 1,
      sm: 2,
      md: 3,
      lg: 4,
    },
  });
  
  const carouselRef = useRef<HTMLDivElement>(null);

  // Function to equalize card heights
  useEffect(() => {
    if (!carouselRef.current) return;
    
    const equalizeHeights = () => {
      const cards = carouselRef.current?.querySelectorAll('.carousel-card');
      if (!cards?.length) return;
      
      // Reset heights to auto first to get natural heights
      cards.forEach((card) => {
        (card as HTMLElement).style.height = 'auto';
      });
      
      // Find the tallest card
      let maxHeight = 0;
      cards.forEach((card) => {
        maxHeight = Math.max(maxHeight, (card as HTMLElement).offsetHeight);
      });
      
      // Apply the height to all cards
      if (maxHeight > 0) {
        cards.forEach((card) => {
          (card as HTMLElement).style.height = `${maxHeight}px`;
        });
      }
    };
    
    // Run on initial render and window resize
    equalizeHeights();
    window.addEventListener('resize', equalizeHeights);
    
    return () => {
      window.removeEventListener('resize', equalizeHeights);
    };
  }, [section]);

  return (
    <Box
      component="section"
      sx={[{ overflow: 'hidden' }, ...(Array.isArray(sx) ? sx : [sx])]}
      {...other}>
      <Container component={MotionViewport} sx={{ textAlign: 'center', py: { xs: 10, md: 1 } }}>
        <m.div variants={varFade('inDown')}>
          <Typography variant="overline" sx={{ color: 'text.disabled' }}>
            {section?.small}
          </Typography>
        </m.div>

        <m.div variants={varFade('inUp')}>
          <Typography variant="h2" sx={{ my: 3, color: 'primary.main' }}>
            {section?.title}
          </Typography>
        </m.div>

        <m.div variants={varFade('inUp')}>
          <Typography sx={{ mx: 'auto', maxWidth: 640, color: 'text.secondary' }}>
            {section?.subtitle}
          </Typography>
        </m.div>

        <Box sx={{ position: 'relative' }} ref={carouselRef}>
          <CarouselArrowFloatButtons {...carousel.arrows} options={carousel.options} sx={{ borderRadius:0}} />

          <Carousel carousel={carousel} sx={{ px: 0.5 }}>
            {section?.cards?.map((card, index) => (
              <Box
                key={`carousel-item-${index}`}
                component={m.div}
                variants={varFade('in')}
                sx={{ py: { xs: 8, md: 10 } }}>
                <Item card={card} />
              </Box>
            ))}
          </Carousel>
        </Box>
      </Container>
    </Box>
  );
}

// ----------------------------------------------------------------------

type ItemProps = {
  card: CardItem;
};

function Item({ card }: ItemProps) {
  const theme = useTheme();
  return (
    <Card className="carousel-card" sx={{ borderRadius: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h6" sx={{ ml: 2, mt: 2, mb: 2, textAlign: 'left', color: 'primary.main' }}>
        {card.title}
      </Typography>

      <Typography variant="body1" sx={{ ml: 2, mb: 2, textAlign: 'left', color: 'text.secondary' }}>
        {card.subtitle}
      </Typography>

      <Box sx={{ px: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {card.image?.url ? (
          <Image
            alt={card.image.title}
            src={card.image.url}
            sx={{ flexGrow: 1, objectFit: 'cover' }}
            onLoad={() => {
              // Trigger re-equalization after image loads
              window.dispatchEvent(new Event('resize'));
            }}
          />
        ) : (
          <Image alt={card.title} ratio="1/1" sx={{ flexGrow: 1 }} />
        )}
      </Box>

      <Box
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'flex-end', // Aligns content to the right
          marginTop: 'auto',
        }}>
        {card.cta1 && (
          <Button size="medium" variant="contained" href={getCtaUrl(card.cta1)}
            sx={{ 
              borderRadius: 0,
              bgcolor: alpha(theme.palette.primary.main, 0.08), // Button background color
              color: theme.palette.primary.main, // Text color
              border: `1px solid alpha(theme.palette.primary.main, 0.2)`, // Border color
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.08), // Background color on hover
                border: `1px solid ${theme.palette.primary.main}`, // Border color on hover
              }, 
            }}
            startIcon={ card.cta1.icon && (
              <Icon name={getIconName(card.cta1.icon, 'unknown')} color={theme.palette.primary.main} />
              )}
            >
            {card?.cta1.title}
          </Button>
        )}
      </Box>
    </Card>
  );
}
